import React, { useState, ReactNode, FC } from 'react';
import {
  AlarmSeverityChip,
  AlarmStatusChip,
  AlarmTypeChip,
  alarmTypeTranslationKey,
  humanizeAlarmType,
  DataGrid,
  DataGridProps,
  GridValueFormatterParams,
} from '@thingslog/ui-components';
import { AlarmType } from '@thingslog/repositories';
import { GridRenderCellParams, GridValueGetterParams } from '@thingslog/ui-components/src/DataGrid';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

const AlarmsTable: FC<AlarmsTableProps> = ({
  pageSize,
  autoHeight,
  rowsPerPageOptions,
  onPageSizeChange,
  ...props
}: AlarmsTableProps) => {
  const { t } = useTranslation();
  const [size, setSize] = useState(10);

  const onPageSizeChangeHandler = (pageSize: number): void => setSize(pageSize);

  return (
    <DataGrid
      pageSize={pageSize ? pageSize : size}
      rowsPerPageOptions={rowsPerPageOptions ? rowsPerPageOptions : [10, 25, 50]}
      autoHeight={autoHeight ? autoHeight : true}
      onPageSizeChange={onPageSizeChange ? onPageSizeChange : onPageSizeChangeHandler}
      columns={[
        {
          field: 'date',
          headerName: t('alarms_export_table_date'),
          type: 'date',
          flex: 0.75,
          minWidth: 140,
          valueFormatter: (params: GridValueFormatterParams<string>) =>
            params.value ? format(new Date(params.value), 'dd MMM yyyy HH:mm') : '',
        },
        {
          field: 'deviceNumber',
          headerName: t('alarms_export_table_device_number'),
          flex: 0.75,
          minWidth: 110,
        },
        {
          field: 'deviceName',
          headerName: t('alarms_export_table_device_name'),
          flex: 0.75,
          minWidth: 150,
        },
        {
          field: 'severity',
          headerName: t('alarms_export_table_severity'),
          flex: 0.5,
          minWidth: 90,
          renderCell: (params: GridRenderCellParams): ReactNode => {
            const severity = params.value ?? null;
            return (
              <AlarmSeverityChip
                size="md"
                value={severity}
                tooltip={
                  severity !== null
                    ? t('alarm_severity_tooltip', { value: severity })
                    : t('alarm_severity_none_tooltip')
                }
              />
            );
          },
        },
        {
          field: 'sensorIndex',
          headerName: t('alarms_export_table_sensor_index'),
          headerAlign: 'left',
          type: 'number',
          flex: 0.6,
          minWidth: 90,
          align: 'center',
        },
        {
          field: 'sensorName',
          headerName: t('alarms_export_table_sensor_name'),
          flex: 0.7,
          minWidth: 150,
        },
        {
          field: 'alarmType',
          headerName: t('alarms_export_table_alarm_type'),
          flex: 0.9,
          minWidth: 180,
          // Sorting and filtering run on the translated text the cell actually shows.
          valueGetter: (params: GridValueGetterParams): string =>
            t(alarmTypeTranslationKey(params.value), {
              defaultValue: humanizeAlarmType(params.value),
            }),
          renderCell: (params: GridRenderCellParams<string>): ReactNode => (
            <AlarmTypeChip
              size="md"
              alarmType={params.row.alarmType as AlarmType}
              label={params.value}
            />
          ),
        },
        {
          field: 'alarmStatusType',
          headerName: t('alarms_export_table_alarm_status'),
          flex: 0.5,
          valueGetter: (params: GridValueGetterParams): string | null => {
            if (params.value) {
              return params.value === 'ACTIVE'
                ? t('alarms_export_chip_status_active').toUpperCase()
                : t('alarms_export_chip_status_inactive').toUpperCase();
            } else {
              return null;
            }
          },
          minWidth: 90,
          renderCell: (params: GridRenderCellParams<string | null>): ReactNode => {
            const status = params.row.alarmStatusType;
            if (params.value === null || !status) return <></>;
            return <AlarmStatusChip size="md" status={status} label={params.value} />;
          },
        },
        {
          field: 'description',
          headerName: t('alarms_export_table_description'),
          flex: 1,
          minWidth: 150,
        },
        {
          field: 'linkToGraph',
          headerName: t('alarms_export_table_link_to_graph'),
          sortable: false,
          width: 50,
          align: 'center',
          renderHeader: () => <></>,
          renderCell: (params: GridRenderCellParams<string | null>): ReactNode => {
            return (
              <QueryStatsIcon
                className="cursor-pointer"
                onClick={(event: React.MouseEvent<SVGSVGElement, MouseEvent>): void => {
                  event.stopPropagation();
                  props.onGraphIconClickCallback(params.row.deviceNumber);
                }}
              />
            );
          },
        },
      ]}
      {...props}
    />
  );
};

interface AlarmsTableProps extends Omit<DataGridProps, 'columns'> {
  onGraphIconClickCallback: (deviceNumber: string) => void;
}

export default AlarmsTable;
